import { GET_COUNTRIES, GET_LANGUAGES, GET_TIMEZONES } from "@apiUtils";
import { hasAuthToken, mutationFetch } from "@atlas/sdk";
import type { IRegionalOption } from "@models";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const LANGUAGES_KEY = "languagesKey";
export const TIMEZONES_KEY = "timezonesKey";
export const COUNTRIES_KEY = "countriesKey";

export const getLanguagesKey = (basePath?: string) =>
  [LANGUAGES_KEY, basePath] as const;

export const getTimezonesKey = (basePath?: string) =>
  [TIMEZONES_KEY, basePath] as const;

export const getCountriesKey = (basePath?: string) =>
  [COUNTRIES_KEY, basePath] as const;

const withBasePath = (path: string, basePath?: string) =>
  basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;

type RegionalQueryOptions<TKey extends readonly unknown[]> = Partial<
  UseQueryOptions<IRegionalOption[], AxiosError, IRegionalOption[], TKey>
>;

const fetchRegionalOptions = async (
  path: string,
  basePath?: string,
): Promise<IRegionalOption[]> => {
  const data = await mutationFetch<IRegionalOption[]>({
    url: withBasePath(path, basePath),
    method: "GET",
  });
  return Array.isArray(data) ? data : [];
};

export const withCurrentRegionalOption = (
  options: IRegionalOption[] | undefined,
  currentValue?: string | null,
): IRegionalOption[] => {
  const items = options ?? [];
  if (!currentValue) return items;
  if (items.some((item) => item.value === currentValue)) return items;
  return [{ value: currentValue, label: currentValue }, ...items];
};

export const useLanguages = (
  basePath?: string,
  options?: RegionalQueryOptions<ReturnType<typeof getLanguagesKey>>,
) => {
  return useQuery({
    queryKey: getLanguagesKey(basePath),
    queryFn: () => fetchRegionalOptions(GET_LANGUAGES, basePath),
    ...options,
    enabled: hasAuthToken() && (options?.enabled ?? true),
  });
};

export const useTimezones = (
  basePath?: string,
  options?: RegionalQueryOptions<ReturnType<typeof getTimezonesKey>>,
) => {
  return useQuery({
    queryKey: getTimezonesKey(basePath),
    queryFn: () => fetchRegionalOptions(GET_TIMEZONES, basePath),
    ...options,
    enabled: hasAuthToken() && (options?.enabled ?? true),
  });
};

export const useCountries = (
  basePath?: string,
  options?: RegionalQueryOptions<ReturnType<typeof getCountriesKey>>,
) => {
  return useQuery({
    queryKey: getCountriesKey(basePath),
    queryFn: () => fetchRegionalOptions(GET_COUNTRIES, basePath),
    ...options,
    enabled: hasAuthToken() && (options?.enabled ?? true),
  });
};
